import React, { useState, useEffect } from 'react';
import WebhookModal from '../components/WebhookModal';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import './Webhooks.css';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchWebhooks, fetchWebhookEvents } from '../store/slices/webhooksSlice';
import api from '../services/api';

const Webhooks = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingWebhook, setEditingWebhook] = useState(null);
  const [webhookToDelete, setWebhookToDelete] = useState(null);
  const [selectedWebhook, setSelectedWebhook] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const dispatch = useAppDispatch();

  // Use webhooks from the redux store
  const { webhooks, loading, webhookEvents, loadingEvents } = useAppSelector(state => state.webhooks);

  useEffect(() => {
    dispatch(fetchWebhooks());
  }, [dispatch]);

  

  // When selected webhook changes, fetch its events
  useEffect(() => {
    if (selectedWebhook) {
      dispatch(fetchWebhookEvents({ id: selectedWebhook.id }));
    }
  }, [selectedWebhook, dispatch]);

  const eventsForSelected = selectedWebhook ? (webhookEvents[selectedWebhook.id] || []) : [];

  const filteredWebhooks = webhooks.filter(webhook =>
    webhook.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    webhook.url.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleAddWebhook = () => {
    setEditingWebhook(null);
    setIsModalOpen(true);
  };

  const handleEditWebhook = (webhook) => {
    setEditingWebhook(webhook);
    setIsModalOpen(true);
  };

  const handleDeleteWebhook = (webhook) => {
    setWebhookToDelete(webhook);
    setIsDeleteModalOpen(true);
  };

  const handleSaveWebhook = async (formData) => {
    try {
      if (editingWebhook) {
        await api.webhooks.update(editingWebhook.id, formData);
      } else {
        await api.webhooks.create(formData);
      }
      dispatch(fetchWebhooks());
    } catch (err) {
      alert('Failed to save webhook');
    }
  };

  const handleConfirmDelete = async () => {
    if (webhookToDelete) {
      try {
        await api.webhooks.delete(webhookToDelete.id);
        dispatch(fetchWebhooks());
      } catch (err) {
        alert('Failed to delete webhook');
      }
      setIsDeleteModalOpen(false);
      setWebhookToDelete(null);
    }
  };

  const handleTestWebhook = async (webhook) => {
    try {
      await api.webhooks.test(webhook.id);
      // Refresh list
      dispatch(fetchWebhooks());
      alert(`Test event sent to ${webhook.name}`);
    } catch (err) {
      alert('Failed to send test event');
    }
  };

  const handleToggleStatus = async (webhook) => {
    try {
      const newStatus = webhook.status === 'active' ? 'inactive' : 'active';
      await api.webhooks.update(webhook.id, { status: newStatus });
      dispatch(fetchWebhooks());
    } catch (err) {
      alert('Failed to update webhook status');
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      'active': { color: '#10b981', bg: '#d1fae5' },
      'inactive': { color: '#6b7280', bg: '#f3f4f6' },
      'error': { color: '#ef4444', bg: '#fee2e2' }
    };

    const statusConfig = config[status] || config['inactive'];
    return (
      <span 
        className="status-badge" 
        style={{ background: statusConfig.bg, color: statusConfig.color }}
      >
        {status.toUpperCase()}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const stats = {
    total: webhooks.length,
    active: webhooks.filter(w => w.status === 'active').length,
    totalEvents: webhooks.reduce((sum, w) => sum + w.totalEvents, 0),
    avgSuccessRate: webhooks.length > 0
      ? (webhooks.reduce((sum, w) => sum + w.successRate, 0) / webhooks.length).toFixed(2)
      : 0
  };

  const filteredEvents = eventsForSelected;

  return (
    <div className="webhooks-container">
      <div className="webhooks-header">
        <div className="header-content">
          <h1>Webhooks</h1>
          <p>Configure and manage webhook endpoints for WhatsApp Business API events</p>
        </div>
        <button className="btn-add-webhook" onClick={handleAddWebhook}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
          Add Webhook
        </button>
      </div>

      <div className="webhooks-stats">
        <div className="stat-card">
          <div className="stat-value">{stats.total}</div>
          <div className="stat-label">Total Webhooks</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.active}</div>
          <div className="stat-label">Active</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.totalEvents.toLocaleString()}</div>
          <div className="stat-label">Total Events</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.avgSuccessRate}%</div>
          <div className="stat-label">Avg Success Rate</div>
        </div>
      </div>

      <div className="webhooks-toolbar">
        <div className="search-box">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"></circle>
            <path d="m21 21-4.35-4.35"></path>
          </svg>
          <input
            type="text"
            placeholder="Search webhooks..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      <div className="webhooks-grid">
        {filteredWebhooks.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🔗</div>
            <h3>No webhooks found</h3>
            <p>Create your first webhook to receive WhatsApp events</p>
            <button className="btn-add-first" onClick={handleAddWebhook}>
              Add Webhook
            </button>
          </div>
        ) : (
          filteredWebhooks.map(webhook => (
            <div key={webhook.id} className="webhook-card">
              <div className="webhook-header">
                <div className="webhook-title">
                  <h3>{webhook.name}</h3>
                  {getStatusBadge(webhook.status)}
                </div>
                <div className="webhook-actions">
                  <button
                    className="action-btn test-btn"
                    onClick={() => handleTestWebhook(webhook)}
                    title="Test Webhook"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polygon points="5 3 19 12 5 21 5 3"></polygon>
                    </svg>
                  </button>
                  <button
                    className="action-btn edit-btn"
                    onClick={() => handleEditWebhook(webhook)}
                    title="Edit"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                    </svg>
                  </button>
                  <button
                    className="action-btn toggle-btn"
                    onClick={() => handleToggleStatus(webhook)}
                    title={webhook.status === 'active' ? 'Deactivate' : 'Activate'}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      {webhook.status === 'active' ? (
                        <path d="M18 6L6 18M6 6l12 12"></path>
                      ) : (
                        <path d="M20 6L9 17l-5-5"></path>
                      )}
                    </svg>
                  </button>
                  <button
                    className="action-btn delete-btn"
                    onClick={() => handleDeleteWebhook(webhook)}
                    title="Delete"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="3 6 5 6 21 6"></polyline>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                    </svg>
                  </button>
                </div>
              </div>

              <div className="webhook-body">
                <div className="webhook-info">
                  <div className="info-row">
                    <span className="info-label">URL:</span>
                    <span className="info-value url-value">{webhook.url}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Verify Token:</span>
                    <span className="info-value token-value">{webhook.verifyToken}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Subscribed Fields:</span>
                    <div className="fields-list">
                      {webhook.subscribedFields.map((field, idx) => (
                        <span key={idx} className="field-badge">{field}</span>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="webhook-stats">
                  <div className="stat-item">
                    <span className="stat-label">Total Events</span>
                    <span className="stat-value">{webhook.totalEvents.toLocaleString()}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Failed</span>
                    <span className="stat-value error">{webhook.failedEvents}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Success Rate</span>
                    <span className="stat-value success">{webhook.successRate.toFixed(2)}%</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Last Event</span>
                    <span className="stat-value">{formatDate(webhook.lastEvent)}</span>
                  </div>
                </div>
              </div>

              <div className="webhook-footer">
                <button
                  className="btn-view-events"
                  onClick={() => setSelectedWebhook(selectedWebhook?.id === webhook.id ? null : webhook)}
                >
                  {selectedWebhook?.id === webhook.id ? 'Hide' : 'View'} Events
                </button>
                <span className="last-test">Last tested: {formatDate(webhook.lastTest)}</span>
              </div>
            </div>
          ))
        )}
      </div>

      {selectedWebhook && (
        <div className="webhook-events-section">
          <div className="events-header">
            <h3>Recent Events - {selectedWebhook.name}</h3>
            <button
              className="btn-close-events"
              onClick={() => setSelectedWebhook(null)}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
          <div className="events-table-container">
            <table className="events-table">
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>Event Type</th>
                  <th>Status</th>
                  <th>Response Code</th>
                  <th>Response Time</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredEvents.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="empty-state-cell">No events found</td>
                  </tr>
                ) : (
                  filteredEvents.slice(0, 20).map(event => (
                    <tr key={event.id}>
                      <td>{formatDate(event.timestamp)}</td>
                      <td><span className="event-type-badge">{event.eventType}</span></td>
                      <td>
                        <span className={`status-badge ${event.status}`}>
                          {event.status}
                        </span>
                      </td>
                      <td>{event.responseCode}</td>
                      <td>{event.responseTime}ms</td>
                      <td>
                        <button
                          className="btn-view-payload"
                          onClick={() => {
                            console.log('Payload:', event.payload);
                            alert('Check console for payload details');
                          }}
                        >
                          View Payload
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <WebhookModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingWebhook(null);
        }}
        onSave={handleSaveWebhook}
        webhook={editingWebhook}
      />

      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setWebhookToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        customerName={webhookToDelete?.name || ''}
        type="webhook"
      />
    </div>
  );
};

export default Webhooks;

